import type { SpawnPositionMode } from './SpawnPositionMode.js';

/**
 * A single wave event exactly as it appears inside a Song Pack v1 `.chart` file.
 *
 * Wire-format notes (verified against both producers; see
 * `docs/songpack-compatibility.md`):
 *
 * - Unity writes this via `JsonUtility.ToJson(ChartDocument)`, which serialises
 *   `Vector2` as `{"x":..,"y":..}`, `Color` as `{"r":..,"g":..,"b":..,"a":..}` and
 *   enums as bare integers. It always emits every field.
 * - The Python engine writes `asdict(ChartEvent)`, whose dataclass fields use the
 *   same nested dict shapes and the same key names.
 *
 * The two encodings are therefore byte-compatible and this module reads both with
 * no conversion step, satisfying requirement 10.
 */

export interface Vector2V1 {
  readonly x: number;
  readonly y: number;
}

export interface ColorV1 {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}

export interface WaveEventV1 {
  /** Seconds from the start of the song. */
  readonly time: number;
  /** Musical beat index. Informational; the runtime schedules on `time`. */
  readonly beat: number;
  readonly initialRadius: number;
  /** Normalised 0..1 loudness at the event, used for scoring and colour. */
  readonly intensity: number;
  readonly spawnPositionMode: SpawnPositionMode;
  readonly origin: Vector2V1;
  readonly fixedWorldPosition: Vector2V1;
  readonly normalizedPosition: Vector2V1;
  readonly edgePadding: number;
  readonly minimumDistanceFromPlayer: number;
  readonly randomSeedOffset: number;
  readonly warningLeadTime: number;
  readonly speed: number;
  readonly gateAngle: number;
  readonly gateCenterAngle: number;
  readonly gateWidth: number;
  readonly damage: number;
  readonly baseScore: number;
  readonly dangerColor: ColorV1;
  readonly gateColor: ColorV1;
  readonly paintColor: ColorV1;
}

export const ZERO_VECTOR2: Vector2V1 = Object.freeze({ x: 0, y: 0 });

/**
 * Unity's `Color` default is fully transparent black, not white. The runtime view
 * layer already treats an all-zero `gateColor` as "use the theme default" — that
 * substitution belongs there, not here, so this stays a faithful zero.
 */
export const ZERO_COLOR: ColorV1 = Object.freeze({ r: 0, g: 0, b: 0, a: 0 });

/**
 * Values used when a key is absent from the JSON.
 *
 * These mirror what `JsonUtility` produces for a missing field on a C# struct with
 * no field initialisers: numeric zero, and zeroed nested values. Choosing Unity's
 * deserialisation behaviour (rather than the Python dataclass defaults, which
 * differ — e.g. `speed = 2.0`) keeps this reader bug-compatible with the engine
 * that defines the `.chart` contract. In practice both producers emit every field,
 * so these only apply to hand-authored or future charts.
 */
export const WAVE_EVENT_DEFAULTS = Object.freeze({
  time: 0,
  beat: 0,
  initialRadius: 0,
  intensity: 0,
  spawnPositionMode: 0,
  edgePadding: 0,
  minimumDistanceFromPlayer: 0,
  randomSeedOffset: 0,
  warningLeadTime: 0,
  speed: 0,
  gateAngle: 0,
  gateCenterAngle: 0,
  gateWidth: 0,
  damage: 0,
  baseScore: 0,
});
